import React, { useState } from 'react';
import { Mail, Phone, MapPin, Download, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data) {
        toast({
          title: "Message Sent!",
          description: "Thank you for your inquiry. Zane will be in touch soon.",
        });
        setFormData({ name: '', email: '', organization: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadCV = () => {
    // Direct download approach - let browser handle it natively
    const downloadUrl = `${API}/cv/download?format=pdf`;
    window.open(downloadUrl, '_blank');
    
    toast({
      title: "Download Started",
      description: "Your CV download has begun.",
    });
  };

  return (
    <section id="contact" className="contact-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Ready for Your Next Leadership Challenge</h2>
          <p className="section-subtitle">Open to Principal and Senior Leadership positions in international schools worldwide</p>
        </div>

        <div className="contact-grid">
          <div className="contact-info">
            <h3>Availability & Preferences</h3>
            <p className="contact-intro">
              <strong>Available:</strong> Immediately for the right opportunity
              <br />
              <strong>Seeking:</strong> Principal, Head of School, or Senior Leadership positions
              <br />
              <strong>Location:</strong> Currently in Cayman Islands | Open to international relocation
              <br />
              <strong>Experience:</strong> UK, Panama, and Caribbean educational contexts
            </p>

            <div className="contact-details">
              <div className="contact-item">
                <MapPin size={24} />
                <div>
                  <h4>Location</h4>
                  <p>Grand Cayman, Cayman Islands</p>
                </div>
              </div>

              <div className="contact-item">
                <Phone size={24} />
                <div>
                  <h4>Phone</h4>
                  <p>+1 345 928 0456</p>
                </div>
              </div>

              <div className="contact-item">
                <Mail size={24} />
                <div>
                  <h4>Email</h4>
                  <p>Available upon request</p>
                </div>
              </div>
            </div>

            <div className="cv-download">
              <Button onClick={handleDownloadCV} className="download-btn">
                <Download size={20} />
                Download Full CV
              </Button>
            </div>

            <div className="qualifications-summary">
              <h4>Education</h4>
              <p><strong>B.A. (Hons): Primary Education with QTS</strong></p>
              <p>Bishop Grosseteste College, Lincoln</p>
            </div>
          </div>

          <div className="contact-form-container">
            <h3>Send a Message</h3>
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="organization">Organization</label>
                <Input
                  id="organization"
                  name="organization"
                  type="text"
                  value={formData.organization}
                  onChange={handleChange}
                  placeholder="School or institution name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Tell me about your inquiry..."
                  rows={5}
                />
              </div>

              <Button type="submit" disabled={isSubmitting} className="submit-btn">
                {isSubmitting ? 'Sending...' : (
                  <>
                    <Send size={20} />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <footer className="site-footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} Zane Powles MBE. All rights reserved.</p>
          <p className="footer-tagline">Transforming education through leadership, compassion, and dedication</p>
        </div>
      </footer>
    </section>
  );
};

export default ContactSection;